import { DbAuditLogAction, DbAuditLogTask } from '@/app/athenaApiGen';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { formatEstTimestamp } from '../../dashboard/components/dateUtils';
import { StyledDataGridPro } from '../../shared-theme/StyledComponents';

type TAuditLogProps = {
  auditLog: DbAuditLogTask[];
};

const AuditLog = (props: TAuditLogProps) => {
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});

  const renderCustomCellContent = (row: DbAuditLogTask, field: string) => {
    const childrenMapper = {
      taskName: 'actionName',
      workflowTaskState: 'workflowTaskState',
      taskOwner: 'actionOwner',
      taskStarted: 'actionStarted',
    };

    const actionField: keyof DbAuditLogAction = childrenMapper[
      field as keyof typeof childrenMapper
    ] as keyof DbAuditLogAction;
    type TAuditKeys = keyof DbAuditLogTask;
    const rowField: Exclude<TAuditKeys, 'actions'> = field as Exclude<
      TAuditKeys,
      'actions'
    >;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          gap: 3,
          justifyContent: 'center',
        }}
      >
        <Box
          title={row[rowField]?.toString()}
          sx={{
            minHeight: '40px',
            alignContent: 'center',
          }}
        >
          <Typography>
            {field === 'taskStarted'
              ? formatEstTimestamp(row.taskStarted)
              : (row[rowField] ?? '-')}
          </Typography>
        </Box>
        {expanded[row.workflowTaskNumber ?? ''] && (
          <Box
            sx={{
              gap: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {row.actions?.map((action, index) => (
              <Box
                key={index}
                sx={{
                  minHeight: '40px',
                  height: '100%',
                  gap: 1,
                  display: 'flex',
                  displayDirection: 'row',
                }}
                title={action[actionField]?.toString()}
              >
                {actionField === 'actionName' && (
                  <Typography>
                    {`${row.workflowTaskNumber}.${index + 1}`}
                  </Typography>
                )}
                <Typography>
                  {actionField === 'actionStarted'
                    ? formatEstTimestamp(action.actionStarted)
                    : (action[actionField] ?? '')}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  useEffect(() => {
    const initialExpandedState: { [key: string]: boolean } = {};
    props.auditLog.forEach((log) => {
      initialExpandedState[log.workflowTaskNumber ?? ''] = false;
    });
    setExpanded(initialExpandedState);
  }, [props.auditLog]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="h5" sx={{ pl: 1 }}>
        Audit Log
      </Typography>
      <StyledDataGridPro
        rows={props.auditLog}
        getRowId={(log) => log.workflowTaskNumber ?? ''}
        getRowHeight={() => 'auto'}
        hideFooter
        columns={[
          {
            field: 'workflowTaskNumber',
            headerName: 'No.',
            flex: 0.5,
            renderCell: ({ row }) => {
              return (
                <Grid
                  container
                  rowSpacing={3}
                  sx={{
                    alignContent: 'center',
                    height: expanded[row.workflowTaskNumber ?? '']
                      ? '40px'
                      : '100%',
                  }}
                >
                  <Grid
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography>{row.workflowTaskNumber ?? '-'}</Typography>

                    <Box
                      sx={{ display: 'flex', cursor: 'pointer' }}
                      onClick={() => {
                        setExpanded((prev) => ({
                          ...prev,
                          [row.workflowTaskNumber ?? '']:
                            !prev[row.workflowTaskNumber ?? ''],
                        }));
                      }}
                    >
                      {expanded[row.workflowTaskNumber ?? ''] ? (
                        <ExpandLess
                          sx={{
                            color: '#0092CB',
                          }}
                        />
                      ) : (
                        <ExpandMore
                          sx={{
                            color: '#0092CB',
                          }}
                        />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              );
            },
          },
          {
            field: 'taskName',
            headerName: 'Task',
            flex: 2,
            renderCell: ({ row }) => renderCustomCellContent(row, 'taskName'),
          },
          {
            field: 'workflowTaskState',
            headerName: 'Status',
            flex: 2,
            renderCell: ({ row }) =>
              renderCustomCellContent(row, 'workflowTaskState'),
          },
          {
            field: 'taskOwner',
            headerName: 'Modified By',
            flex: 1,
            renderCell: ({ row }) => renderCustomCellContent(row, 'taskOwner'),
          },

          {
            field: 'taskStarted',
            headerName: 'Start',
            // Fixed to fit the timestamp ("DD MMM YYYY h:mm a") so the
            // Comment column absorbs the remaining width in expanded mode.
            width: 180,
            renderCell: ({ row }) =>
              renderCustomCellContent(row, 'taskStarted'),
          },
          {
            field: 'comment',
            headerName: 'Comment',
            flex: 3,
            renderCell: ({ row }) => {
              return (
                <Grid
                  container
                  rowSpacing={3}
                  sx={{
                    alignContent: 'center',
                    height: expanded[row.workflowTaskNumber ?? '']
                      ? '40px'
                      : '100%',
                  }}
                >
                  <Grid
                    sx={{
                      minHeight: '40px',
                      maxHeight: '80px',
                      overflowY: 'auto',
                      width: '100%',
                    }}
                  >
                    <Typography
                      sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                    >
                      {row.comment ?? '-'}
                    </Typography>
                  </Grid>
                </Grid>
              );
            },
          },
        ]}
      />
    </Box>
  );
};

export default AuditLog;
